import { createBrowserRouter, Navigate, Outlet, ScrollRestoration } from "react-router";
import { CookieConsent } from "@/components/consent/cookie-consent";
import { GoogleAnalytics } from "@/components/consent/google-analytics";
import { LocaleSync } from "@/components/locale-select";
import { AuthShell } from "@/components/auth/auth-shell";
import { SessionFallback } from "@/components/page-states";
import { Guard } from "@shared/client/session";
import { Toaster } from "@shared/components/ui/sonner";
import { onboardingRule, productRule, guestRule } from "./rules";
import ForgotPasswordPage from "./pages/auth/forgot-password";
import LoginPage from "./pages/auth/login";
import ResetPasswordPage from "./pages/auth/reset-password";
import SignupPage from "./pages/auth/signup";
import DashboardPage from "./pages/dashboard";
import LeadsPage from "./pages/leads";
import NotFoundPage, { AppNotFoundPage } from "./pages/not-found";
import OfferPage from "./pages/offer";
import OnboardingPage from "./pages/onboarding";
import ProductLayout from "./pages/product-layout";
import ReportPage from "./pages/report";
import ReportsPage from "./pages/reports";
import RouteErrorPage from "./pages/route-error";
import SettingsPage from "./pages/settings";
import StopPage from "./pages/stop";
import EmailPreferencesPage from "./pages/email-preferences";

function Root() {
  return (
    <>
      <LocaleSync />
      <Outlet />
      {/* Issue des actions longues (enquête, rédaction) ; sous md, au-dessus de la barre d'onglets. */}
      <Toaster className="max-md:[--toast-bottom:calc(4rem+env(safe-area-inset-bottom)+12px)]" />
      <CookieConsent />
      <GoogleAnalytics />
      <ScrollRestoration />
    </>
  );
}

export const router = createBrowserRouter([
  {
    element: <Root />,
    // Un écran qui plante au rendu : une page d'erreur calme, jamais la trace
    // (après un déploiement, l'onglet périmé se recharge d'abord).
    errorElement: <RouteErrorPage />,
    children: [
      // La vitrine vit sur le domaine principal (site/) : la racine de l'app mène à l'espace.
      { path: "/", element: <Navigate replace to="/app" /> },
      {
        path: "/auth",
        element: (
          <AuthShell>
            <Outlet />
          </AuthShell>
        ),
        children: [
          // Session illisible : l'erreur et « Réessayer » au lieu d'une carte vide.
          { path: "login", element: <Guard fallback={<SessionFallback />} rule={guestRule}><LoginPage /></Guard> },
          { path: "signup", element: <Guard fallback={<SessionFallback />} rule={guestRule}><SignupPage /></Guard> },
          { path: "forgot-password", element: <ForgotPasswordPage /> },
          { path: "reset-password", element: <ResetPasswordPage /> },
        ],
      },
      { path: "/onboarding", element: <Guard fallback={<SessionFallback standalone />} rule={onboardingRule}><OnboardingPage /></Guard> },
      {
        path: "/app",
        // Le cadre s'affiche si la session tarde ; une erreur de rendu reste dans le cadre.
        element: <Guard fallback={<ProductLayout session="pending" />} rule={productRule}><ProductLayout /></Guard>,
        errorElement: <RouteErrorPage inShell />,
        children: [
          { index: true, element: <DashboardPage /> },
          { path: "leads", element: <LeadsPage /> },
          { path: "reports", element: <ReportsPage /> },
          { path: "reports/:id", element: <ReportPage /> },
          { path: "offer", element: <OfferPage /> },
          { path: "settings", element: <SettingsPage /> },
          { path: "*", element: <AppNotFoundPage /> },
        ],
      },
      // Désinscription des e-mails de prospection (lien de chaque e-mail), sans session.
      { path: "/stop/:token", element: <StopPage /> },
      // Désinscription du récapitulatif hebdomadaire (lien de chaque e-mail produit), sans session.
      { path: "/preferences/emails/:token", element: <EmailPreferencesPage /> },
      { path: "*", element: <NotFoundPage /> },
    ],
  },
]);
